import { Action, createReducer, on } from '@ngrx/store';
import { FlightSearchForm } from '../../../models/flight-search-forms/flight-search-form';
import {
  addUpdateFlightSearchDepartureDateData,
  addUpdateFlightSearchFormData,
  addUpdateFlightSearchFormRoutingData,
  addUpdateFlightSearchJuniorJetsetterData,
  initializeFlightSearchFormsState,
  removeFlightSearchFormRoutingData,
  resetFlightSearchFormData,
  resetSelectedFlightResults,
  setFlightSearchCurrentRouteIdForFeature,
  setOverrideClassOfService,
  setSelectedFlightResult,
} from './flight-search-forms.actions';
import { flightSearchFormsAdapter, FlightSearchFormsState, initialFlightSearchFormsState } from './flight-search-forms.state';

/**
 * Helper function to update or add flight search form data
 */
function updateOrAddFlightSearchFormData(state: FlightSearchFormsState, action, updateFunction) {
  const { featureId, flightSearchFormData } = action;
  const existingFlightSearchForm = state.flightSearchForms.entities[featureId];

  if (!existingFlightSearchForm) {
    const newFlightSearchForm = {
      featureId,
      airports: [flightSearchFormData],
    };
    return {
      ...state,
      flightSearchForms: flightSearchFormsAdapter.setOne(newFlightSearchForm, state.flightSearchForms),
    };
  }

  let updatedForm = existingFlightSearchForm.airports?.map((airportForm) => updateFunction(airportForm, flightSearchFormData));

  const wasUpdated = updatedForm?.some((airportForm) => airportForm.id === flightSearchFormData.id);
  if (!wasUpdated) {
    updatedForm = updatedForm?.length > 0 ? updatedForm : [];
    updatedForm.push(flightSearchFormData);
  }

  const updatedFlightSearchForm = {
    ...existingFlightSearchForm,
    airports: updatedForm,
  };

  return {
    ...state,
    flightSearchForms: flightSearchFormsAdapter.setOne(updatedFlightSearchForm, state.flightSearchForms),
  };
}

/**
 * Handles all state changes, there is no way to change the state without a reducer AND the reducer never
 * modifies state, it clones the state from the store, changes that cloned state in some way, and then replaces
 * the entire old state with the cloned and modified state.
 */
const featureReducer = createReducer(
  initialFlightSearchFormsState,
  /**
   * Reinitialize the flight search forms state
   */
  on(initializeFlightSearchFormsState, (state) => ({ ...state, ...initialFlightSearchFormsState })),
  /**
   * Reset the flight search form data for a given feature ID
   */
  on(resetFlightSearchFormData, (state, { featureId }) => {
    const updatedFlightSearchForms = state.flightSearchForms.entities[featureId];
    if (!updatedFlightSearchForms) {
      return state;
    }

    return {
      ...state,
      flightSearchForms: flightSearchFormsAdapter.removeOne(featureId, state.flightSearchForms),
    };
  }),

  /**
   * Reset the selected flight results for a given feature ID
   */
  on(resetSelectedFlightResults, (state, { featureId }) => {
    const existingFlightSearchForm = state.flightSearchForms.entities[featureId];
    if (!existingFlightSearchForm) {
      return state;
    }

    const updatedFlightSearchForm = {
      ...existingFlightSearchForm,
      selectedFlightResults: [],
    };

    return {
      ...state,
      flightSearchForms: flightSearchFormsAdapter.setOne(updatedFlightSearchForm, state.flightSearchForms),
    };
  }),

  /**
   * Set the flight search current route ID for a feature
   */
  on(setFlightSearchCurrentRouteIdForFeature, (state, { featureId, currentRouteId }) => {
    const existingFlightSearchForm = state.flightSearchForms.entities[featureId];
    if (!existingFlightSearchForm) {
      return state;
    }

    // ***Important***
    // Remove any selected flight results for routes from and after the current route
    // New search new selection!
    // Otherwise FRAPI or PricingApi will break!
    const updatedSelectedFlightResults = existingFlightSearchForm.selectedFlightResults?.filter(
      (result) => result.routeId < currentRouteId
    );

    const updatedFlightSearchForm = {
      ...existingFlightSearchForm,
      currentRouteId,
      selectedFlightResults: updatedSelectedFlightResults,
    } as FlightSearchForm;

    return {
      ...state,
      flightSearchForms: flightSearchFormsAdapter.setOne(updatedFlightSearchForm, state.flightSearchForms),
    };
  }),

  /**
   * Add/update a flight search form route/date data
   */
  on(addUpdateFlightSearchFormRoutingData, (state, action) =>
    updateOrAddFlightSearchFormData(state, action, (airportForm, flightSearchFormData) =>
      airportForm.id === flightSearchFormData.id
        ? {
            ...flightSearchFormData,
            departureDateValid: airportForm.departureDateValid,
            departureDateValue: airportForm.departureDateValue,
            returnDateValid: airportForm.returnDateValid,
            returnDateValue: airportForm.returnDateValue,
          }
        : airportForm
    )
  ),
  on(removeFlightSearchFormRoutingData, (state, { featureId, routeId }) => {
    const existingFlightSearchForm = state.flightSearchForms.entities[featureId];
    if (!existingFlightSearchForm) {
      return state;
    }

    const filteredAirports = existingFlightSearchForm.airports?.filter((airport) => airport.id !== routeId);

    // Reorder the IDs to be consecutive starting from 0.
    const updatedAirports = filteredAirports?.map((airport, index) => ({
      ...airport,
      id: index,
    }));

    const updatedFlightSearchForm = {
      ...existingFlightSearchForm,
      airports: updatedAirports,
    };

    return {
      ...state,
      flightSearchForms: flightSearchFormsAdapter.setOne(updatedFlightSearchForm, state.flightSearchForms),
    };
  }),
  /**
   * Add/update a flight search form departure date data
   */
  on(addUpdateFlightSearchDepartureDateData, (state, action) =>
    updateOrAddFlightSearchFormData(state, action, (airportForm, flightSearchFormData) =>
      airportForm.id === flightSearchFormData.id
        ? {
            ...flightSearchFormData,
            departureAirportValid: airportForm.departureAirportValid,
            departureAirportValue: airportForm.departureAirportValue,
            arrivalAirportValid: airportForm.arrivalAirportValid,
            arrivalAirportValue: airportForm.arrivalAirportValue,
            departureAirportTimeZone: airportForm.departureAirportTimeZone,
            arrivalAirportTimeZone: airportForm.arrivalAirportTimeZone,
          }
        : airportForm
    )
  ),
  on(addUpdateFlightSearchJuniorJetsetterData, (state, { featureId, juniorJetsetterForm }) => {
    const existingFlightSearchForm = state.flightSearchForms.entities[featureId];

    // If the flight search form does not exist, create a new one with junior jetsetter data
    if (!existingFlightSearchForm) {
      const newFlightSearchForm: FlightSearchForm = {
        featureId,
        juniorJetsetterForm,
      };
      return {
        ...state,
        flightSearchForms: flightSearchFormsAdapter.setOne(newFlightSearchForm, state.flightSearchForms),
      };
    }

    // If the flight search form exists, update the junior jetsetter data
    const updatedFlightSearchForm: FlightSearchForm = {
      ...existingFlightSearchForm,
      juniorJetsetterForm,
    };

    return {
      ...state,
      flightSearchForms: flightSearchFormsAdapter.setOne(updatedFlightSearchForm, state.flightSearchForms),
    };
  }),
  /**
   * Add/update a flight search form data
   */
  on(addUpdateFlightSearchFormData, (state, { featureId, flightSearchFormData }) => {
    const existingFlightSearchForm = state.flightSearchForms.entities[featureId];

    // If the flight search form does not exist, create a new one
    if (!existingFlightSearchForm) {
      const newFlightSearchForm = {
        featureId,
        ...flightSearchFormData, // Use the incoming flightSearchFormData
      };
      return {
        ...state,
        flightSearchForms: flightSearchFormsAdapter.setOne(newFlightSearchForm, state.flightSearchForms),
      };
    }

    // If the flight search form exists, replace the airports array with the new one
    const updatedForm: FlightSearchForm = {
      ...existingFlightSearchForm,
      ...flightSearchFormData, // Merge the incoming data
      airports: flightSearchFormData.airports ?? existingFlightSearchForm.airports, // Retain existing airports if not provided
    };
    return {
      ...state,
      flightSearchForms: flightSearchFormsAdapter.setOne(updatedForm, state.flightSearchForms),
    };
  }),
  on(setSelectedFlightResult, (state, { featureId, selectedFlightResult }) => {
    const existingFlightSearchForm = state.flightSearchForms.entities[featureId];
    const existingSelectedFlightResults = existingFlightSearchForm?.selectedFlightResults ?? [];

    // Check if the selectedFlightResult already exists in existingSelectedFlightResults
    const existingIndex = existingSelectedFlightResults.findIndex((result) => result.routeId === selectedFlightResult.routeId);

    let updatedSelectedFlightResults;
    if (existingIndex !== -1) {
      // If a selection was made, update the existing selection object
      updatedSelectedFlightResults = existingSelectedFlightResults.map((result, index) =>
        index === existingIndex ? selectedFlightResult : result
      );
    } else {
      // Else, add the new object.
      updatedSelectedFlightResults = [...existingSelectedFlightResults, selectedFlightResult];
    }
    return {
      ...state,
      flightSearchForms: flightSearchFormsAdapter.updateOne(
        {
          id: featureId,
          changes: {
            selectedFlightResults: updatedSelectedFlightResults,
          },
        },
        state.flightSearchForms
      ),
    };
  }),
  on(setOverrideClassOfService, (state, { featureId, classOfService }) => {
    const existingFlightSearchForm = state.flightSearchForms.entities[featureId];
    if (!existingFlightSearchForm) {
      return state;
    }
    const updatedFlightSearchForm = {
      ...existingFlightSearchForm,
      overrideClassOfService: classOfService,
    };
    return {
      ...state,
      flightSearchForms: flightSearchFormsAdapter.setOne(updatedFlightSearchForm, state.flightSearchForms),
    };
  })
);

/**
 * Typescript safe way to export the reducer so that it can be imported in modules
 */
export function flightSearchFormsReducer(state = initialFlightSearchFormsState, action: Action) {
  return featureReducer(state, action);
}
